/**
 * "What's new" slide-over panel — OMN-368.
 *
 * Right-hand slide-over (same shell idiom as MaintenanceReportDetailDrawer).
 * Renders once, above the mobile nav drawer (z-[60] > drawer's z-50). Text-only
 * entries for v1 (no deep-linking). Footer routes to the feedback channel.
 */
import React, { useEffect } from 'react';
import { X, Sparkles } from 'lucide-react';
import { format, parseISO } from 'date-fns';
import { useWhatsNew } from './WhatsNewContext';
import { ChangelogTag } from './ChangelogTag';
import { pickLocale } from './whatsNewData';
import { openFeedbackMail } from '../FeedbackButton';
import { useLanguage } from '../../contexts/LanguageContext';

export const WhatsNewPanel: React.FC = () => {
  const { isOpen, close, entries } = useWhatsNew();
  const { t, language } = useLanguage();

  // Close on Escape while open.
  useEffect(() => {
    if (!isOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') close();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [isOpen, close]);

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 z-[60] flex justify-end"
      role="dialog"
      aria-modal="true"
      aria-label={t('whatsNew.title')}
    >
      {/* Backdrop */}
      <div className="fixed inset-0 bg-black bg-opacity-50" onClick={close} aria-hidden="true" />

      {/* Panel */}
      <div className="relative bg-white w-full max-w-md h-full shadow-xl flex flex-col">
        <header className="flex items-center justify-between px-5 py-4 border-b border-gray-200">
          <div className="flex items-center gap-2">
            <Sparkles className="h-5 w-5 text-blue-600" />
            <h2 className="text-lg font-semibold text-gray-900">{t('whatsNew.title')}</h2>
          </div>
          <button
            type="button"
            onClick={close}
            className="p-1 rounded text-gray-500 hover:text-gray-700 hover:bg-gray-100"
            aria-label={t('whatsNew.close')}
          >
            <X className="h-5 w-5" />
          </button>
        </header>

        <div className="flex-1 overflow-y-auto px-5 py-2">
          {entries.length === 0 ? (
            <p className="py-8 text-center text-sm text-gray-500">{t('whatsNew.empty')}</p>
          ) : (
            <ul className="divide-y divide-gray-100">
              {entries.map((entry) => (
                <li key={entry.id} className="py-4">
                  <div className="flex items-center justify-between gap-3 mb-1.5">
                    <ChangelogTag tag={entry.tag} />
                    <time className="text-xs text-gray-400" dateTime={entry.date}>
                      {format(parseISO(entry.date), 'MMM d, yyyy')}
                    </time>
                  </div>
                  <h3 className="text-sm font-semibold text-gray-900">{pickLocale(entry.title, language)}</h3>
                  <p className="mt-1 text-sm text-gray-600 leading-relaxed">{pickLocale(entry.body, language)}</p>
                </li>
              ))}
            </ul>
          )}
        </div>

        <footer className="px-5 py-4 border-t border-gray-200 text-sm text-gray-600">
          {t('whatsNew.footerPrompt')}{' '}
          <button
            type="button"
            onClick={openFeedbackMail}
            className="font-medium text-blue-600 hover:text-blue-700 underline underline-offset-2"
          >
            {t('whatsNew.footerCta')}
          </button>
        </footer>
      </div>
    </div>
  );
};
